import { useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const MAX_SCALE = 20000000;
const THRESHOLDS = { yellow: 1000000, orange: 5000000, red: 20000000 };
const TICK_Y = (THRESHOLDS.yellow / MAX_SCALE) * 100;
const TICK_O = (THRESHOLDS.orange / MAX_SCALE) * 100;

const NB_MONTHS = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${d.getDate()}. ${NB_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const parseNum = (str) => {
  if (!str) return 0;
  const s = String(str).trim();
  if (s.startsWith('<') || s === '' || s === '0') return 0;
  return parseFloat(s.replace(/[\s\u00A0]/g, '').replace(/,/g, '.')) || 0;
};
const formatVal = (v) => { if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `${Math.round(v/1e3)}k`; return v.toLocaleString('nb-NO'); };
const computeColor = (n) => { if (n <= 0) return 'green'; if (n >= THRESHOLDS.red) return 'red'; if (n >= THRESHOLDS.orange) return 'orange'; if (n >= THRESHOLDS.yellow) return 'yellow'; return 'green'; };

const C = {
  green:  { bar: '#10b981', text: '#059669', light: '#ecfdf5' },
  yellow: { bar: '#eab308', text: '#a16207', light: '#fefce8' },
  orange: { bar: '#fb923c', text: '#c2410c', light: '#fff7ed' },
  red:    { bar: '#ef4444', text: '#dc2626', light: '#fef2f2' },
};

function Bar({ value, height, showTicks }) {
  const strVal = String(value || '').trim();
  const isBelow = strVal.startsWith('<');
  const numVal = parseNum(strVal);
  const ck = isBelow ? 'green' : computeColor(numVal);
  const pct = Math.min((numVal / MAX_SCALE) * 100, 100);
  const c = C[ck];
  return (
    <div>
      <div className="relative w-full rounded-full" style={{ height, backgroundColor: 'rgba(0,0,0,0.04)' }}>
        <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${isBelow ? 1.5 : Math.max(pct, 2)}%`, backgroundColor: c.bar, transition: 'width 0.4s ease' }} />
        {showTicks && <>
          <div className="absolute top-0 h-full" style={{ left: `${TICK_Y}%`, width: 1, backgroundColor: 'rgba(234,179,8,0.35)' }} />
          <div className="absolute top-0 h-full" style={{ left: `${TICK_O}%`, width: 1, backgroundColor: 'rgba(249,115,22,0.35)' }} />
        </>}
      </div>
    </div>
  );
}

function CellBar({ data, isSum, onClick }) {
  if (!data) return <td className="text-center text-slate-200 text-xs py-3 px-2">–</td>;
  const strVal = String(data.value).trim();
  const isZeroish = strVal.startsWith('<') || strVal === '0' || strVal === '';
  const numVal = parseNum(strVal);
  const ck = isZeroish ? 'green' : computeColor(numVal);
  const c = C[ck];
  return (
    <td className="py-2 px-2 cursor-pointer group" style={{ minWidth: 90 }} onClick={onClick}>
      <div className="group-hover:bg-slate-50 rounded-lg p-1 -m-1 transition-colors">
        <div className="text-right mb-1">
          <span className={`font-mono ${isSum ? 'font-bold text-xs' : 'font-semibold text-[11px]'}`} style={{ color: c.text }}>
            {isZeroish ? '~0' : formatVal(numVal)}
          </span>
        </div>
        <Bar value={data.value} height={isSum ? 6 : 5} showTicks />
      </div>
    </td>
  );
}

// === DETAIL MODAL ===
function DetailModal({ organism, location, locFullName, allBulks, onClose }) {
  const species = organism.split('_').slice(1).join('_');
  const isSum = species.toUpperCase().includes('SUM');
  const displayName = isSum ? 'SUM Kiselalgar' : species;

  // Get all historical values for this organism+location from all bulks
  const history = useMemo(() => {
    if (!allBulks?.length) return [];
    return allBulks
      .map(bulk => {
        const orgData = bulk.algae_data?.[organism];
        if (!orgData) return null;
        // Find the matching location key
        let val = null;
        for (const [locKey, locVal] of Object.entries(orgData)) {
          const clean = locKey.includes(' - ') ? locKey.split(' - ')[1] : locKey;
          if (clean === location || locKey === locFullName) {
            val = locVal;
            break;
          }
        }
        if (val === null || val === undefined || String(val).trim() === '') return null;
        return { date: bulk.date, value: val, area: bulk.area_name, id: bulk.id };
      })
      .filter(Boolean)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [allBulks, organism, location, locFullName]);

  const [page, setPage] = useState(0);
  const perPage = 8;
  const pageCount = Math.ceil(history.length / perPage);
  const visible = history.slice(page * perPage, (page + 1) * perPage);

  // Find max value across all history for consistent scale
  const maxHistVal = Math.max(MAX_SCALE, ...history.map(h => parseNum(h.value)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-lg text-slate-900">{displayName}</h3>
            <p className="text-sm text-slate-500">{locFullName || location}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400"><X className="w-5 h-5" /></button>
        </div>

        {/* History bars */}
        <div className="px-6 py-5">
          {history.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Ingen historiske data funne</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-slate-500 font-medium">
                  {history.length} måling{history.length !== 1 ? 'ar' : ''} · Viser {page * perPage + 1}–{Math.min((page + 1) * perPage, history.length)}
                </span>
                {pageCount > 1 && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-xs text-slate-400 px-1">{page + 1}/{pageCount}</span>
                    <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {visible.map((entry, i) => {
                  const numVal = parseNum(entry.value);
                  const isBelow = String(entry.value).trim().startsWith('<');
                  const ck = isBelow ? 'green' : computeColor(numVal);
                  const c = C[ck];
                  const pct = Math.min((numVal / maxHistVal) * 100, 100);
                  const isFirst = page === 0 && i === 0;

                  return (
                    <div key={entry.id + entry.date} className={`rounded-xl p-3 ${isFirst ? 'ring-2 ring-teal-200 bg-teal-50/30' : 'bg-slate-50/50'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isFirst ? 'text-teal-700' : 'text-slate-700'}`}>{fmtDate(entry.date)}</span>
                          {isFirst && <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-medium">Siste</span>}
                        </div>
                        <span className="font-mono font-bold text-sm" style={{ color: c.text }}>
                          {isBelow ? '~0' : formatVal(numVal)}
                        </span>
                      </div>
                      <div className="relative w-full h-3 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                        <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${isBelow ? 1 : Math.max(pct, 1.5)}%`, backgroundColor: c.bar, transition: 'width 0.4s ease' }} />
                        <div className="absolute top-0 h-full" style={{ left: `${(THRESHOLDS.yellow / maxHistVal) * 100}%`, width: 1, backgroundColor: 'rgba(234,179,8,0.3)' }} />
                        <div className="absolute top-0 h-full" style={{ left: `${(THRESHOLDS.orange / maxHistVal) * 100}%`, width: 1, backgroundColor: 'rgba(249,115,22,0.3)' }} />
                        <div className="absolute top-0 h-full" style={{ left: `${(THRESHOLDS.red / maxHistVal) * 100}%`, width: 1, backgroundColor: 'rgba(239,68,68,0.3)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Scale reference */}
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                <span className="text-[8px] text-slate-300">SKALA</span>
                <div className="relative flex-1 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, #d1fae5, #fef9c3 25%, #ffedd5 50%, #fee2e2 100%)' }}>
                  <div className="absolute top-[-1px] bottom-[-1px]" style={{ left: `${(THRESHOLDS.yellow / maxHistVal) * 100}%`, width: 1, backgroundColor: '#ca8a04' }} />
                  <div className="absolute top-[-1px] bottom-[-1px]" style={{ left: `${(THRESHOLDS.orange / maxHistVal) * 100}%`, width: 1, backgroundColor: '#fb923c' }} />
                  <div className="absolute top-[-1px] bottom-[-1px]" style={{ left: `${(THRESHOLDS.red / maxHistVal) * 100}%`, width: 1, backgroundColor: '#ef4444' }} />
                </div>
                <div className="flex gap-1.5 text-[8px] text-slate-300">
                  <span className="text-yellow-600/60">1M</span><span className="text-orange-600/60">5M</span><span className="text-red-500/60">20M</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// === MAIN TABLE ===
export default function OrganismOverviewTable({ mapLocations, allBulks }) {
  const [modal, setModal] = useState(null); // { organism, location, locFullName }

  const { orgKeys, sortedLocs } = useMemo(() => {
    if (!mapLocations?.length) return { orgKeys: [], sortedLocs: [] };
    const keys = new Set();
    mapLocations.forEach(loc => { if (loc.organismData) Object.keys(loc.organismData).forEach(k => { if (k.startsWith('Kiselalger_')) keys.add(k); }); });
    let sum = null;
    const regular = [];
    [...keys].sort().forEach(k => { if (k.toUpperCase().includes('SUM')) sum = k; else regular.push(k); });
    const allKeys = sum ? [...regular, sum] : regular;
    return { orgKeys: allKeys, sortedLocs: [...mapLocations].sort((a, b) => (a.letter || '').localeCompare(b.letter || '')) };
  }, [mapLocations]);

  if (orgKeys.length === 0) return null;
  const tableW = sortedLocs.length * 100 + 150;

  return (
    <div className="mb-6" data-testid="organism-overview-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">Kiselalgar — Siste måling</h2>
          <p className="text-xs text-slate-500 mt-0.5">Klikk på ein celle for å sjå historikk · Terskelmarkørar ved 1M og 5M</p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          {[['green','Grøn'],['yellow','Gul'],['orange','Oransje'],['red','Raud']].map(([k,l]) => (
            <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-1.5 rounded-sm" style={{ backgroundColor: C[k].bar }} /><span className="text-slate-400">{l}</span></span>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm">
        <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
          <table className="w-full border-collapse" style={{ minWidth: tableW }}>
            <thead>
              <tr>
                <th className="text-left py-3 px-4 text-[10px] font-semibold text-slate-400 uppercase tracking-widest sticky left-0 bg-white z-10 border-b border-slate-100" style={{ minWidth: 140 }}>
                  Organisme
                </th>
                {sortedLocs.map(loc => (
                  <th key={loc.name} className="text-center py-3 px-2 border-b border-slate-100" style={{ minWidth: 90 }}>
                    <div className="text-xs font-bold text-slate-700">{loc.letter}</div>
                    <div className="text-[9px] text-slate-400 mt-0.5 truncate">{loc.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orgKeys.map((orgKey, i) => {
                const species = orgKey.split('_').slice(1).join('_');
                const isSum = species.toUpperCase().includes('SUM');
                return (
                  <tr key={orgKey} className={`${isSum ? 'bg-slate-50/80 border-t-2 border-slate-200' : i % 2 ? '' : ''} hover:bg-slate-50/60 transition-colors`}>
                    <td className={`py-2.5 px-4 sticky left-0 z-10 border-b border-slate-50 ${isSum ? 'bg-slate-50/80' : 'bg-white'}`}>
                      <span className={`text-xs ${isSum ? 'font-bold text-slate-900' : 'text-slate-700 font-medium'}`}>
                        {isSum ? 'SUM Kiselalgar' : species}
                      </span>
                    </td>
                    {sortedLocs.map(loc => (
                      <CellBar
                        key={loc.name}
                        data={loc.organismData?.[orgKey]}
                        isSum={isSum}
                        onClick={() => setModal({ organism: orgKey, location: loc.name, locFullName: loc.fullName })}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Scale */}
        <div className="px-4 py-2.5 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-[8px] text-slate-300 shrink-0">SKALA</span>
            <div className="relative flex-1 h-1.5 rounded-full" style={{ background: 'linear-gradient(90deg, #d1fae5 0%, #d1fae5 5%, #fef9c3 5%, #fef9c3 25%, #ffedd5 25%, #ffedd5 100%)' }}>
              <div className="absolute top-[-2px] bottom-[-2px]" style={{ left: `${TICK_Y}%`, width: 1, backgroundColor: '#ca8a04' }} />
              <div className="absolute top-[-2px] bottom-[-2px]" style={{ left: `${TICK_O}%`, width: 1, backgroundColor: '#fb923c' }} />
            </div>
            <div className="flex gap-2 text-[8px] text-slate-300 shrink-0">
              <span>0</span><span className="text-yellow-600/60">1M</span><span className="text-orange-600/60">5M</span><span className="text-red-500/60">20M</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {modal && (
        <DetailModal
          organism={modal.organism}
          location={modal.location}
          locFullName={modal.locFullName}
          allBulks={allBulks}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
